"use client";

import { useSelection } from "@trydig/centra/client";
import { useEffect } from "react";
import type { UserData } from "~/lib/gtm/types";
import { sendGTMEvent } from "~/lib/gtm/utils";

const sha256Hash = async (text: string): Promise<string> => {
	const encoder = new TextEncoder();
	const data = encoder.encode(text);
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

const USER_DATA_KEY = "events_user_data";

export const getUserData = (): UserData | undefined => {
	if (typeof window === "undefined") {
		console.error("Attempted to call getUserData on server side");
		return undefined;
	}
	const stored = sessionStorage.getItem(USER_DATA_KEY);
	if (!stored) return undefined;

	try {
		return JSON.parse(stored) as UserData;
	} catch {
		return undefined;
	}
};

export const setUserData = async (userData: UserData) => {
	const oldUserData = getUserData();
	const newUserData = { ...oldUserData, ...userData };

	if (newUserData.email) {
		newUserData.sha256_hashed_email = await sha256Hash(newUserData.email);
	}
	if (newUserData.phone_number) {
		newUserData.sha256_hashed_phone_number = await sha256Hash(newUserData.phone_number);
	}
	if (newUserData.first_name) {
		newUserData.sha256_hashed_first_name = await sha256Hash(newUserData.first_name);
	}
	if (newUserData.last_name) {
		newUserData.sha256_hashed_last_name = await sha256Hash(newUserData.last_name);
	}
	if (newUserData.street) {
		newUserData.sha256_hashed_street = await sha256Hash(newUserData.street);
	}
	if (newUserData.city) {
		newUserData.sha256_hashed_city = await sha256Hash(newUserData.city);
	}
	if (newUserData.country) {
		newUserData.sha256_hashed_country = await sha256Hash(newUserData.country);
	}
	if (newUserData.postal_code) {
		newUserData.sha256_hashed_postal_code = await sha256Hash(newUserData.postal_code);
	}

	sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(newUserData));

	sendGTMEvent({
		event: "user_data_updated",
		user_data: newUserData,
	});

	return newUserData;
};

const useSyncGTMUserData = () => {
	const { data: selection } = useSelection();

	useEffect(() => {
		const updateUserData = async () => {
			const userData: UserData = {};

			if (selection?.selection?.address?.email) {
				userData.email = selection.selection.address.email;
			}

			if (selection?.selection?.address?.phoneNumber) {
				userData.phone_number = selection.selection.address.phoneNumber;
			}

			if (selection?.selection?.address?.firstName) {
				userData.first_name = selection.selection.address.firstName;
			}

			if (selection?.selection?.address?.lastName) {
				userData.last_name = selection.selection.address.lastName;
			}

			if (selection?.selection?.address?.address1) {
				userData.street = selection.selection.address.address1;
			}

			if (selection?.selection?.address?.city) {
				userData.city = selection.selection.address.city;
			}

			if (selection?.selection?.address?.country) {
				userData.country = selection.selection.address.country;
			}

			if (selection?.selection?.address?.zipCode) {
				userData.postal_code = selection.selection.address.zipCode;
			}

			if (Object.keys(userData).length > 0) {
				await setUserData(userData);
			}
		};

		void updateUserData();
	}, [
		selection?.selection?.address?.email,
		selection?.selection?.address?.phoneNumber,
		selection?.selection?.address?.firstName,
		selection?.selection?.address?.lastName,
		selection?.selection?.address?.address1,
		selection?.selection?.address?.city,
		selection?.selection?.address?.country,
		selection?.selection?.address?.zipCode,
	]);
};

export const SyncGTMUserData = () => {
	useSyncGTMUserData();

	return null;
};
